import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text } from '../../components/shared/Text';
import IntentionInput from './components/IntentionInput';
import BirthDetailsCollector from './components/BirthDetailsCollector';
import LocalStorage from '../../services/storage/LocalStorage';
import { validateIntentionText, validateAtLeastOneIntention } from '../../utils/validation';
import { createIntention } from '../../models/Intention';

/**
 * Onboarding Screen
 * Allows users to set their personal intentions
 * Collects birth details in background (not displayed)
 */
export const OnboardingScreen: React.FC = () => {
  const navigate = useNavigate();
  const [intentions, setIntentions] = useState<string[]>(['']);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleIntentionChange = (index: number, value: string) => {
    const newIntentions = [...intentions];
    newIntentions[index] = value;
    setIntentions(newIntentions);
    
    // Clear error for this field
    if (errors[index]) {
      const newErrors: Record<number, string> = {};
      Object.keys(errors).forEach((key) => {
        const numKey = Number(key);
        if (numKey !== index) {
          newErrors[numKey] = errors[numKey];
        }
      });
      setErrors(newErrors);
    }
  };

  const removeIntentionField = (index: number) => {
    if (intentions.length > 1) {
      const newIntentions = intentions.filter((_, i) => i !== index);
      setIntentions(newIntentions);
      const newErrors: Record<number, string> = {};
      Object.keys(errors).forEach((key) => {
        const numKey = Number(key);
        if (numKey !== index) {
          newErrors[numKey] = errors[numKey];
        }
      });
      // Reindex errors for remaining fields
      const reindexedErrors: Record<number, string> = {};
      Object.keys(newErrors).forEach((key) => {
        const numKey = Number(key);
        if (numKey > index) {
          reindexedErrors[numKey - 1] = newErrors[numKey];
        } else {
          reindexedErrors[numKey] = newErrors[numKey];
        }
      });
      setErrors(reindexedErrors);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    // Validate all intentions
    const validationErrors: Record<number, string> = {};
    const validatedIntentions = intentions
      .map((text, index) => {
        const validation = validateIntentionText(text);
        if (!validation.valid && validation.error) {
          validationErrors[index] = validation.error;
        }
        return validation.valid ? text.trim() : null;
      })
      .filter((text): text is string => text !== null && text.length > 0);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    // Check at least one intention
    const atLeastOneValidation = validateAtLeastOneIntention(validatedIntentions);
    if (!atLeastOneValidation.valid) {
      if (atLeastOneValidation.error) {
        alert(atLeastOneValidation.error);
      }
      setIsSubmitting(false);
      return;
    }

    // Create intention objects
    const intentionObjects = validatedIntentions.map((text) => createIntention(text));

    // Store intentions
    LocalStorage.setItem(LocalStorage.getKeys().INTENTIONS, intentionObjects);

    // Collect birth details in background (simulated)
    BirthDetailsCollector.collect();

    // Navigate to main interface
    navigate('/affirm');
  };

  return (
    <div 
      className="min-h-screen p-screen-padding flex flex-col items-center justify-center relative pb-24"
      style={{
        backgroundImage: 'url(/images/onboarding/onboarding.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay for better text readability */}
      <div 
        className="absolute inset-0 bg-black/60"
        style={{ zIndex: 0 }}
      />
      
      <div className="w-full max-w-md relative flex flex-col" style={{ zIndex: 1, gap: '20px' }}>
        <div 
          style={{
            textAlign: 'center',
            background: 'linear-gradient(to right, #FFA147, #FE5706)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: '24px',
            fontFamily: 'Didot',
            fontWeight: 400,
            lineHeight: '28.80px',
            wordWrap: 'break-word',
          }}
        >
          SET INTENTION
        </div>

        <div
          style={{
            width: '313px',
            textAlign: 'center',
            color: '#E6E6E6',
            fontSize: '16px',
            fontFamily: 'Space Grotesk',
            fontWeight: 400,
            lineHeight: '32px',
            wordWrap: 'break-word',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          What do you want to focus on?
        </div>

        {intentions.map((intention, index) => (
          <div key={index}>
            <IntentionInput
              value={intention}
              onChange={(value) => handleIntentionChange(index, value)}
              onRemove={() => removeIntentionField(index)}
              canRemove={intentions.length > 1}
              error={errors[index]}
              placeholder="Enter your intention..."
            />
          </div>
        ))}
      </div>

      <button
        onClick={handleSubmit}
        disabled={isSubmitting}
        className="absolute bottom-8 left-0 right-0 flex items-center justify-center transition-opacity active:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          fontFamily: 'Space Grotesk',
          fontStyle: 'normal',
          fontWeight: 400,
          fontSize: '16px',
          lineHeight: '24px',
          color: '#FFFFFF',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '12px 24px',
        }}
        aria-label="Complete onboarding"
      >
        {isSubmitting ? 'Setting up...' : 'Continue'}
      </button>
    </div>
  );
};

export default OnboardingScreen;
